import {
  USAGES_STARTED,
  USAGES_QUEUED,
  USAGES_SCENARIO_STARTED,
  USAGES_SCENARIO_COMPLETED,
  USAGES_SCENARIO_FAILED,
  USAGES_ALL_COMPLETED,
  USAGES_ALL_FAILED,
  USAGES_JOB_DISMISS,
  USAGES_PHASE,
} from "../types";

// Frames that open the run and carry its ETA (etaSecondsRemaining: weight × measured s/row).
const RUN_START_STATUSES = new Set(["STARTED", "QUEUED", "PROJECT_STARTED"]);
// The regeneration's phases; LOAD_BADGES carries its own current/total, which drives the bar.
const RUN_PHASE_STATUSES = new Set(["PREPARING", "SIMULATING", "APPLYING", "LOAD_BADGES"]);

const scenarioName = (getState, scenarioId) => {
  const state = getState()?.scenario;
  const same = (s) => s && String(s.id) === String(scenarioId);
  if (same(state?.item)) return state.item.name || "";
  return (state?.items || []).find(same)?.name || "";
};

/**
 * The "Updating usages" progress card of a regeneration that runs in the background after a
 * Date of Review change (PDR-0141): the dialog closes as soon as the new date is saved, the
 * card tells the user that the usages are still being recomputed and how it ended. Keyed by
 * `jobId` (the backend job, or a client key for the sync regeneration). Plain actions only:
 * the update-usages slice must not import the schedule actions back. `etaSeconds` seeds the
 * estimate when the job's own first frames went out before the follow began (the DoR response).
 */
export function startRegenerationCard(
  dispatch,
  getState,
  scenarioId,
  jobId,
  label = scenarioName(getState, scenarioId),
  { etaSeconds = null } = {},
) {
  dispatch({
    type: USAGES_STARTED,
    payload: { jobId, scenarioId, kind: "updateUsages", startedAt: Date.now() },
  });
  dispatch({
    type: USAGES_QUEUED,
    payload: { jobId, total: 1, projects: [{ index: 0, label }], etaSeconds },
  });
  return {
    // A non-terminal frame of the followed job: its ETA, then the phase it is in.
    progress: (frame) => {
      if (RUN_START_STATUSES.has(frame?.status)) {
        dispatch({
          type: USAGES_SCENARIO_STARTED,
          payload: {
            jobId,
            index: 0,
            label,
            weight: frame.weight,
            startedAt: frame.startedAt,
            etaSeconds: frame.etaSecondsRemaining ?? null,
          },
        });
      } else if (RUN_PHASE_STATUSES.has(frame?.status)) {
        dispatch({
          type: USAGES_PHASE,
          payload: { jobId, phase: frame.status, current: frame.current, total: frame.total },
        });
      }
    },
    completed: () => {
      dispatch({ type: USAGES_SCENARIO_COMPLETED, payload: { jobId, index: 0, label } });
      dispatch({ type: USAGES_ALL_COMPLETED, payload: { jobId } });
    },
    failed: (error = null) => {
      dispatch({ type: USAGES_SCENARIO_FAILED, payload: { jobId, index: 0, label, error } });
      dispatch({ type: USAGES_ALL_FAILED, payload: { jobId, error } });
    },
    // Waiting for the scenario lock: `startedBy` names who holds it (the card says so when
    // known); `waiting(null)` once the request is admitted.
    waiting: (info) =>
      dispatch({
        type: USAGES_PHASE,
        payload: { jobId, phase: info ? "WAITING" : null, startedBy: info?.startedBy ?? null },
      }),
    // The outcome is unknown (aborted, still running past the budget): the card cannot say
    // "done" or "failed", so it goes away.
    dismiss: () => dispatch({ type: USAGES_JOB_DISMISS, payload: { jobId } }),
  };
}

/**
 * The same card around a regeneration followed elsewhere — the multi-project / programme
 * Gantt's « Update usages » and Date of Review alignment, which know the project's name but
 * have no project open in the store. `wait` resolves when the job is done and rejects on
 * failure (an `aborted` rejection, the page being left, removes the card); the rejection is
 * passed on so the caller still counts the failure. `wait` only polls the job's status, so
 * its ETA and phases come from `watch` (watchJobProgress, opened before the request that
 * started the job) and `etaSeconds` (the DoR response's estimate). The watch is stopped here.
 */
export async function withRegenerationCard(dispatch, { scenarioId, jobId, label, etaSeconds = null, watch = null }, wait) {
  const card = startRegenerationCard(
    dispatch,
    null,
    scenarioId,
    jobId ?? `regeneration-${scenarioId}-${Date.now()}`,
    label,
    { etaSeconds },
  );
  watch?.follow(jobId, card.progress);
  try {
    await wait();
    card.completed();
  } catch (err) {
    if (err?.aborted) card.dismiss();
    else card.failed(err?.message ?? null);
    throw err;
  } finally {
    watch?.stop();
  }
}
